import { ItemWithIdentifier, SortOption } from "../types";

/**
 * Sort items based on sort options
 */
export function sortItems<TData extends ItemWithIdentifier>(items: TData[], sortOptions: SortOption[]): TData[] {
  if (!sortOptions.length) return items;

  return [...items].sort((a, b) => {
    for (const sort of sortOptions) {
      const aValue = getNestedValue(a, sort.field);
      const bValue = getNestedValue(b, sort.field);

      const comparison = compareValues(aValue, bValue);

      if (comparison !== 0) {
        return sort.direction === "desc" ? -comparison : comparison;
      }
    }
    return 0;
  });
}

/**
 * Get nested value from object using dot notation
 */
// eslint-disable-next-line typescript/no-explicit-any
function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((current, key) => current?.[key], obj);
}

/**
 * Compare two values for sorting
 */
// eslint-disable-next-line typescript/no-explicit-any
function compareValues(a: any, b: any): number {
  // Handle null/undefined values
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  // Convert to strings and use localeCompare for proper sorting
  const aStr = String(a).toLowerCase();
  const bStr = String(b).toLowerCase();

  return aStr.localeCompare(bStr);
}

/**
 * Filter items based on search term
 */
export function filterItems<TData extends ItemWithIdentifier>(
  items: TData[],
  searchTerm: string,
  searchFields?: string[],
): TData[] {
  if (!searchTerm.trim()) return items;

  const searchLower = searchTerm.toLowerCase();

  return items.filter((item) => {
    if (searchFields && searchFields.length > 0) {
      // Search in specific fields
      return searchFields.some((field) => {
        const value = getNestedValue(item, field);
        return value && String(value).toLowerCase().includes(searchLower);
      });
    } else {
      // Search in all string values
      return Object.values(item).some((value) => value && String(value).toLowerCase().includes(searchLower));
    }
  });
}

/**
 * Apply filters to items
 */
export function applyFilters<TData extends ItemWithIdentifier>(
  items: TData[],
  filterValues: Record<string, string[]>,
): TData[] {
  let filteredItems = items;

  Object.entries(filterValues).forEach(([filterId, values]) => {
    if (values.length > 0) {
      filteredItems = filteredItems.filter((item) => {
        const itemValue = getNestedValue(item, filterId);
        return values.includes(String(itemValue));
      });
    }
  });

  return filteredItems;
}

/**
 * Paginate items
 */
export function paginateItems<TData extends ItemWithIdentifier>(
  items: TData[],
  page: number,
  pageSize: number,
): TData[] {
  const startIndex = (page - 1) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
}
